/** Home della Gestione Tornei: elenco tornei, import da Playtomic, Road To, staff. */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Download, KeyRound, Plus, Trophy } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Tabs } from "@/components/ui/Tabs";
import { cn } from "@/lib/utils";
import { errMsg, fmtWhen, gare, type GaraSummary, type PointsConfig } from "@/lib/gare";
import { garaPath, Section, StatusBadge, useActor, useBase, useFlash } from "./ui";
import { usePendingSync } from "./offline";

type Tab = "tornei" | "roadto" | "staff";

export default function GareHome() {
  const actor = useActor();
  const [tab, setTab] = useState<Tab>("tornei");
  const items = [
    { value: "tornei" as const, label: "Tornei" },
    { value: "roadto" as const, label: "Road To" },
    ...(actor.data?.admin ? [{ value: "staff" as const, label: "Staff" }] : []),
  ];
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Gestione Tornei</h1>
        <p className="text-sm text-slate-500">Iscritti da Playtomic, coppie, risultati in diretta e classifica Road To.</p>
      </div>
      <Tabs<Tab> items={items} value={tab} onChange={setTab} />
      {tab === "tornei" ? <TorneiTab /> : null}
      {tab === "roadto" ? <RoadToTab admin={!!actor.data?.admin} /> : null}
      {tab === "staff" && actor.data?.admin ? <StaffTab /> : null}
    </div>
  );
}

function TorneiTab() {
  const base = useBase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  usePendingSync((pg, st) => {
    qc.setQueryData(["gare", "gara", pg], st);
    qc.invalidateQueries({ queryKey: ["gare", "list"] });
  }, show);
  const [importOpen, setImportOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const list = useQuery({ queryKey: ["gare", "list"], queryFn: gare.list, refetchInterval: 30_000 });
  const upcoming = useQuery({ queryKey: ["gare", "upcoming"], queryFn: gare.upcoming, enabled: importOpen, staleTime: 120_000 });
  const [busyTid, setBusyTid] = useState<string | null>(null);
  const [testName, setTestName] = useState("TEST — prova gestione torneo");

  const open = (id: number) => nav(garaPath(base, id));
  const doImport = async (tid: string) => {
    setBusyTid(tid);
    try {
      const s = await gare.importPlaytomic(tid);
      qc.invalidateQueries({ queryKey: ["gare"] });
      open(s.gara.id);
    } catch (e) {
      show("error", errMsg(e));
    } finally {
      setBusyTid(null);
    }
  };

  const groups: [string, GaraSummary[]][] = [
    ["In corso", (list.data || []).filter((x) => x.status === "live")],
    ["Da preparare", (list.data || []).filter((x) => x.status === "setup")],
    ["Conclusi", (list.data || []).filter((x) => x.status === "finished")],
  ];

  return (
    <div className="space-y-4">
      {flash}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setImportOpen((v) => !v)}>
          <Download className="h-4 w-4" /> Importa da Playtomic
          <ChevronDown className={cn("h-4 w-4 transition-transform", importOpen && "rotate-180")} />
        </Button>
        <Button variant="outline" onClick={() => setTestOpen((v) => !v)}><Plus className="h-4 w-4" /> Torneo di prova</Button>
      </div>

      {importOpen ? (
        <Section title="Tornei Moving su Playtomic (prossime settimane)">
          {upcoming.isLoading ? <p className="text-sm text-slate-500">Leggo il calendario da Playtomic…</p> : null}
          {upcoming.isError ? <p className="text-sm text-red-600">{errMsg(upcoming.error)}</p> : null}
          <ul className="divide-y divide-slate-100">
            {(upcoming.data || []).map((t) => (
              <li key={t.tid} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">
                    {fmtWhen(t.start_at)} · {t.registered}/{t.max_players ?? "?"} iscritti{t.level ? ` · ${t.level}` : ""}
                  </div>
                </div>
                {t.imported_id ? (
                  <Button size="sm" variant="outline" onClick={() => open(t.imported_id!)}>Apri</Button>
                ) : (
                  <Button size="sm" loading={busyTid === t.tid} disabled={!!busyTid} onClick={() => doImport(t.tid)}>Importa</Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {testOpen ? (
        <Section title="Torneo di prova (senza Playtomic)">
          <form className="flex flex-wrap items-end gap-2" onSubmit={async (e) => {
            e.preventDefault();
            try {
              const s = await gare.create({ name: testName, road_to: false, start_at: new Date().toISOString() });
              qc.invalidateQueries({ queryKey: ["gare"] });
              open(s.gara.id);
            } catch (err) {
              show("error", errMsg(err));
            }
          }}>
            <div className="min-w-[14rem] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="t-name">Nome</label>
              <Input id="t-name" value={testName} onChange={(e) => setTestName(e.target.value)} />
            </div>
            <Button type="submit">Crea</Button>
          </form>
          <p className="mt-2 text-xs text-slate-500">Non assegna punti Road To. Aggiungi i giocatori a mano (anche nomi finti) per provare.</p>
        </Section>
      ) : null}

      {list.isLoading ? <p className="text-sm text-slate-500">Caricamento…</p> : null}
      {list.data && !list.data.length ? (
        <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Nessun torneo ancora. Importane uno da Playtomic.</p>
      ) : null}
      {groups.map(([title, rows]) => rows.length ? (
        <div key={title}>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
          <div className="grid gap-2 md:grid-cols-2">
            {rows.map((g) => (
              <Link key={g.id} to={garaPath(base, g.id)}
                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-colors hover:border-moving-400">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900">{g.name}</span>
                  <StatusBadge status={g.status} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {[fmtWhen(g.start_at), g.category, g.format_label].filter(Boolean).join(" · ")}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  <Badge>{g.n_players} giocatori{g.format !== "giro_a_8" ? ` · ${g.n_teams} coppie` : ""}</Badge>
                  {g.n_matches ? <Badge variant="info">{g.n_done}/{g.n_matches} partite</Badge> : null}
                  {g.road_to ? <Badge variant="court">Road To</Badge> : null}
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null)}
    </div>
  );
}

function RoadToTab({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const rt = useQuery({ queryKey: ["gare", "roadto"], queryFn: gare.roadto });
  const [open, setOpen] = useState<string | null>(null);
  if (rt.isLoading) return <p className="text-sm text-slate-500">Caricamento…</p>;
  if (rt.isError || !rt.data) return <p className="text-sm text-red-600">{errMsg(rt.error)}</p>;
  const d = rt.data;
  return (
    <div className="space-y-4">
      {flash}
      <div className="rounded-xl bg-gradient-to-r from-court-600 to-court-500 p-4 text-white">
        <div className="flex items-center gap-2 text-lg font-bold"><Trophy className="h-5 w-5" /> {d.season.name}</div>
        <div className="text-sm text-white/85">
          Dal {new Date(d.season.start).toLocaleDateString("it-IT")} al {new Date(d.season.end).toLocaleDateString("it-IT")}
          {" · "}{d.tournaments === 1 ? "1 torneo concluso" : `${d.tournaments} tornei conclusi`} · {d.ranking.length} giocatori in classifica
        </div>
      </div>
      {!d.ranking.length ? (
        <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">La classifica si riempie quando chiudi il primo torneo valido per il Road To.</p>
      ) : (
        <ol className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {d.ranking.map((e) => (
            <li key={`${e.rank}-${e.name}`} className="border-t border-slate-100 first:border-t-0">
              <button type="button" onClick={() => setOpen(open === e.name ? null : e.name)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50">
                <span className="w-8 text-center text-sm font-bold text-slate-500">{e.rank}°</span>
                <span className="flex-1 text-sm font-medium text-slate-900">{e.name}</span>
                <span className="hidden text-xs text-slate-500 sm:inline">
                  {e.played} tornei{e.wins ? ` · ${e.wins} vittorie` : ""}{e.podiums ? ` · ${e.podiums} podi` : ""}
                </span>
                <span className="w-16 text-right text-sm font-bold tabular-nums text-moving-700">{e.points} pt</span>
              </button>
              {open === e.name ? (
                <ul className="bg-slate-50 px-12 py-2 text-xs text-slate-600">
                  {e.history.map((h) => (
                    <li key={h.gara_id} className="flex justify-between py-0.5">
                      <span>{new Date(h.day).toLocaleDateString("it-IT")} · {h.name} — {h.position}° su {h.players} giocatori</span>
                      <span className="font-semibold">{h.points} pt</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      <PointsForm cfg={d.points} admin={admin} onSave={async (p) => {
        try {
          const r = await gare.setPoints(p);
          qc.setQueryData(["gare", "roadto"], r);
          show("success", "Formula aggiornata: classifica ricalcolata");
        } catch (e) {
          show("error", errMsg(e));
        }
      }} />
    </div>
  );
}

function PointsForm({ cfg, admin, onSave }: { cfg: PointsConfig; admin: boolean; onSave: (p: PointsConfig) => void }) {
  const [v, setV] = useState({
    presence: String(cfg.presence), per: String(cfg.per_player_behind),
    p1: String(cfg.podium["1"] ?? 0), p2: String(cfg.podium["2"] ?? 0), p3: String(cfg.podium["3"] ?? 0),
  });
  const field = (k: keyof typeof v, label: string) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor={`pf-${k}`}>{label}</label>
      <Input id={`pf-${k}`} inputMode="numeric" value={v[k]} disabled={!admin}
        onChange={(e) => setV({ ...v, [k]: e.target.value.replace(/\D/g, "") })} />
    </div>
  );
  return (
    <Section title="Come si calcolano i punti (versione di prova)">
      <p className="mb-3 text-sm text-slate-600">
        Ogni giocatore prende <b>{cfg.presence}</b> punti di presenza, più <b>{cfg.per_player_behind}</b> punti per ogni giocatore
        classificato dietro di lui, più il bonus podio (1° {cfg.podium["1"] ?? 0}, 2° {cfg.podium["2"] ?? 0}, 3° {cfg.podium["3"] ?? 0}).
        Nelle coppie i due giocatori prendono gli stessi punti. Se cambi la formula, tutta la classifica viene ricalcolata.
      </p>
      {admin ? (
        <form className="grid grid-cols-2 gap-3 sm:grid-cols-5" onSubmit={(e) => {
          e.preventDefault();
          onSave({ presence: Number(v.presence || 0), per_player_behind: Number(v.per || 0),
            podium: { "1": Number(v.p1 || 0), "2": Number(v.p2 || 0), "3": Number(v.p3 || 0) } });
        }}>
          {field("presence", "Presenza")}
          {field("per", "Per giocatore dietro")}
          {field("p1", "Bonus 1°")}
          {field("p2", "Bonus 2°")}
          {field("p3", "Bonus 3°")}
          <Button type="submit" className="col-span-2 sm:col-span-5">Salva formula</Button>
        </form>
      ) : null}
    </Section>
  );
}

function StaffTab() {
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const staff = useQuery({ queryKey: ["gare", "staff"], queryFn: gare.staff });
  const [pins, setPins] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const save = async (b: { name: string; pin?: string; active?: boolean }, ok: string) => {
    try {
      qc.setQueryData(["gare", "staff"], await gare.setStaff(b));
      show("success", ok);
      setPins((p) => ({ ...p, [b.name]: "" }));
    } catch (e) {
      show("error", errMsg(e));
    }
  };
  const link = `${window.location.origin}/staff`;
  return (
    <div className="space-y-4">
      {flash}
      <Section title="Accessi dello staff">
        <p className="mb-3 text-sm text-slate-600">
          Ognuno entra da <a className="font-medium text-court-600 underline" href={link}>{link}</a> scegliendo il suo nome e il PIN.
          Vede solo la Gestione Tornei. Imposta qui il PIN (4-8 cifre) e comunicalo a voce.
        </p>
        <ul className="divide-y divide-slate-100">
          {(staff.data || []).map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 py-2.5">
              <span className="w-28 text-sm font-medium text-slate-900">{s.name}</span>
              {s.has_pin ? <Badge variant="success">PIN impostato</Badge> : <Badge variant="warning">senza PIN</Badge>}
              {!s.active ? <Badge variant="danger">disattivato</Badge> : null}
              <form className="ml-auto flex items-center gap-1.5" onSubmit={(e) => {
                e.preventDefault();
                save({ name: s.name, pin: pins[s.name] }, `PIN di ${s.name} salvato`);
              }}>
                <Input type="password" inputMode="numeric" autoComplete="new-password" placeholder="nuovo PIN"
                  aria-label={`Nuovo PIN per ${s.name}`} className="h-9 w-28"
                  value={pins[s.name] || ""} onChange={(e) => setPins({ ...pins, [s.name]: e.target.value.replace(/\D/g, "") })} />
                <Button size="sm" type="submit" disabled={(pins[s.name] || "").length < 4}><KeyRound className="h-3.5 w-3.5" /> Salva</Button>
                <Button size="sm" variant="ghost" type="button"
                  onClick={() => save({ name: s.name, active: !s.active }, s.active ? `${s.name} disattivato` : `${s.name} riattivato`)}>
                  {s.active ? "Disattiva" : "Riattiva"}
                </Button>
              </form>
            </li>
          ))}
        </ul>
        <form className="mt-3 flex gap-2 border-t border-slate-100 pt-3" onSubmit={(e) => {
          e.preventDefault();
          if (newName.trim()) save({ name: newName.trim() }, `${newName} aggiunto`).then(() => setNewName(""));
        }}>
          <Input placeholder="Aggiungi una persona (es. Luca R.)" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Button type="submit" variant="outline">Aggiungi</Button>
        </form>
      </Section>
    </div>
  );
}
